import { useEffect, useState } from "react"
import { useParams } from "react-router-dom"

import Layout from '../../Layout'
// Import components
import ProductGallery from "components/Products/ProductGallery"
import Information from "components/Products/Information"
import Comment from "components/Products/Comment"
import ProductsRecommendation from "components/Products/ProductsRecommendation"

// Import API
import { fetchProductDetail } from "api/ProductDetail"
import { Product } from "components/Products/ProductsList"
import ListProductByCate from "components/Products/ListProductByCate"

// ==================== 1. DEFINITIONS & INTERFACES ====================


export interface Promotion {
    id: number;
    name: string;
    slug: string;
    start_date: string;
    end_date: string;
    is_active: number;
    created_at: string;
    updated_at: string;
}

export interface PromotionFlashSale {
    id: number;
    name: string;
    start_time: string;
    end_time: string;
}

export interface Voucher {
    id: number;
    name: string;
    code: string;
    describe: string;
    is_active: number;
    type: number;
    value: number;
    position: number;
    exp_date: string;
    max_discount: number;
    min_amount: number;
    type_customer: number;
    max_use_voucher: number;
    customer_usage_limit: number;
    used_voucher: number;
    avatar: string | null;
    start_date: string;
}

export interface ProductData {
    id: number;
    name: string;
    slug: string;
    price: number;
    discount?: number;
    avatar: string;
    list_thumbnail: string;

    short_content?: string;
    content?: string;

    seo_page?: string;

    is_active: number;
    is_highlight: number;
    is_follow: number;
    is_new: number;
    is_limit: number;
    is_stock: number;

    star: number;
    position: number;
    point: number;

    created_at: string;
    updated_at: string;

    product_category?: {
        id: number;
        name: string;
        slug: string;
        voucher_product?: Array<{
            target_id: number;
            voucher_id: number;
            voucher: Voucher | null;
        }>;
    };
    product_category_id?: number;

    promotion_flash_sale_product?: Array<{
        product_id: number;
        promotion_flash_sale_id: number;
        percent: number;
        promotion_flash_sale?: PromotionFlashSale | null;
    }>;

    promotion_product?: Array<{
        product_id: number;
        promotion_id: number;
        percent: number;
        promotion?: Promotion | null;
    }>;

    voucher_product?: Array<{
        target_id: number;
        voucher_id: number;
        voucher: Voucher | null;
    }>;

    ingredients?: string;
    indications?: string;
    contraindications?: string;
    dosage?: string;
    drug_interactions?: string;
    storage?: string;
    overdose?: string;
}

// ==================== 2. MAIN COMPONENT ====================

export default function Dashboard() {
    const { slug } = useParams<{ slug: string }>();

    // State lưu trữ dữ liệu thô từ API (hoặc đã qua xử lý nhẹ nếu cần)
    const [product, setProduct] = useState<ProductData | null>(null);
    const [loading, setLoading] = useState<boolean>(true);

    const saveToVisited = (product: Product) => {
        try {
            const stored = localStorage.getItem("visited_products");
            let visited: Product[] = stored ? JSON.parse(stored) : [];
            // Remove duplicate if exists
            visited = visited.filter((p) => p.id !== product.id);
            // Add to the beginning
            visited.unshift(product);
            // Limit size
            if (visited.length > 20) visited = visited.slice(0, 20);
            localStorage.setItem("visited_products", JSON.stringify(visited));
        } catch (error) {
            console.error("Failed to save visited product", error);
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            if (!slug) {
                setLoading(false);
                return;
            }

            setLoading(true);
            try {
                let userId = undefined;
                const userStr = localStorage.getItem('user');

                if (userStr) {
                    try {
                        const userObj = JSON.parse(userStr);
                        userId = userObj.id;
                    } catch (e) {
                        console.error("Lỗi parse user từ localStorage", e);
                    }
                }

                const response = await fetchProductDetail(slug, userId);

                if (response?.data) {
                    // Xử lý linh hoạt cho cả 2 trường hợp API trả về:
                    // 1. Trả về Object chi tiết sản phẩm (response.data)
                    // 2. Trả về Mảng danh sách sản phẩm (response.data.items) - khi dùng filter

                    let rawData: ProductData | null = null;

                    // Kiểm tra nếu data có thuộc tính 'items' và là mảng
                    if ((response.data as any).items && Array.isArray((response.data as any).items)) {
                        const items = (response.data as any).items;
                        if (items.length > 0) {
                            rawData = items[0] as ProductData;
                        }
                    } else {
                        // Trường hợp trả về object trực tiếp
                        rawData = response.data as unknown as ProductData;
                    }

                    if (rawData && rawData.id) {
                        setProduct(rawData);
                        // Lưu vào danh sách đã xem
                        const prodForVisited: Product = {
                            id: rawData.id,
                            name: rawData.name,
                            unit: "box",
                            image: rawData.avatar,
                            price: rawData.price,
                            promoPrice: rawData.price - (rawData.discount ? (rawData.discount / 100) * rawData.price : 0),
                            discount: typeof rawData.discount === "string" ? parseFloat(rawData.discount) : rawData.discount,
                            sold: 0,
                            seller: "Dược Tâm An",
                            sellerLogo: "",
                            quantity: 0,
                            liked: false,
                            bg: false,
                            slug: rawData.slug
                        };
                        console.log('raw data:', rawData);
                        console.log("Saving to visited:", prodForVisited);
                        saveToVisited(prodForVisited);
                    } else {
                        setProduct(null);
                    }
                } else {
                    setProduct(null);
                }
            } catch (error) {
                console.error("Failed to fetch product", error);
                setProduct(null);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [slug]);

    // ==================== RENDER UI ====================

    if (loading) {
        return (
            <Layout>
                <div className="flex justify-center items-center h-[50vh]">
                    Đang tải dữ liệu...
                </div>
            </Layout>
        );
    }

    if (!product) {
        return (
            <Layout>
                <div className="flex justify-center items-center h-[50vh] flex-col gap-2">
                    <h2 className="text-xl font-bold">Không tìm thấy sản phẩm</h2>
                    <a href="/products" className="text-blue-500 hover:underline">Quay lại danh sách</a>
                </div>
            </Layout>
        );
    }

    const contentHtml = {
        description: product.content || "",
        ingredients: product.ingredients || "",
        indications: product.indications || "",
        contraindications: product.contraindications || "",
        dosage: product.dosage || "",
        drug_interactions: product.drug_interactions || "",
        storage: product.storage || "",
        overdose: product.overdose || "",
    };

    return (
        <Layout>
            <div className="styles_wrapperContainer__zxrSf">
                <div className="MuiContainer-root MuiContainer-disableGutters MuiContainer-maxWidthLg" style={{ maxWidth: 1180 }}>

                    {/* Breadcrumbs Navigation */}
                    <div style={{ marginBottom: "10px" }}>
                        <nav className="MuiTypography-root MuiBreadcrumbs-root MuiTypography-body1 MuiTypography-colorTextSecondary">
                            <ol className="MuiBreadcrumbs-ol">
                                <li className="MuiBreadcrumbs-li"><a className="styles_item_breadcrumb__eYGFd" href="/">Trang chủ</a></li>
                                <li className="MuiBreadcrumbs-separator">
                                    <svg className="MuiSvgIcon-root MuiSvgIcon-fontSizeSmall" focusable="false" viewBox="0 0 24 24" aria-hidden="true"><path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" /></svg>
                                </li>
                                <li className="MuiBreadcrumbs-li"><a className="styles_item_breadcrumb__eYGFd" href="/products">Sản phẩm</a></li>
                                <li className="MuiBreadcrumbs-separator">
                                    <svg className="MuiSvgIcon-root MuiSvgIcon-fontSizeSmall" focusable="false" viewBox="0 0 24 24" aria-hidden="true"><path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" /></svg>
                                </li>

                                {product.product_category && (
                                    <>
                                        <li className="MuiBreadcrumbs-li">
                                            <a className="styles_item_breadcrumb__eYGFd" href={`/category/${product.product_category.slug}`}>
                                                {product.product_category.name}
                                            </a>
                                        </li>
                                        <li className="MuiBreadcrumbs-separator">
                                            <svg className="MuiSvgIcon-root MuiSvgIcon-fontSizeSmall" focusable="false" viewBox="0 0 24 24" aria-hidden="true"><path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" /></svg>
                                        </li>
                                    </>
                                )}

                                <li className="MuiBreadcrumbs-li">
                                    <span className="styles_item_breadcrumb__eYGFd styles_last_breadcrumb__c7IQm font-bold">
                                        {product.name}
                                    </span>
                                </li>
                            </ol>
                        </nav>
                    </div>

                    {/* Product Gallery */}
                    <ProductGallery product={product} />

                    {/* Information Detail Sections */}
                    <Information contentHtml={contentHtml} />

                    {/* Comments & Recommendations */}
                    <Comment />
                    {/* Lưu ý: Kiểm tra component ProductsRecommendation nhận prop tên là gì (thường là categoryId hoặc product) */}
                    {/* <ProductsRecommendation categoryId={product.product_category?.id} /> */}

                    <div id="BLOCK_RECOMMEND" event-block="BLOCK_RECOMMEND" className="[&>section]:p-0 [&>section]:mt-0 rounded-[10px] mt-4">
                        <section className="styles_container__tcm_S MuiBox-root css-yd8sa2 !p-0 !mt-0" id="just-for-you">
                            <div className="styles_wrapperTitle__0iTyg MuiBox-root css-1lekzkb">
                                <div className="MuiBox-root css-j0ozid">
                                    <h2 className="styles_sectionHeader__IJC9w">
                                        Sản phẩm cùng danh mục
                                    </h2>
                                </div>

                                <a
                                    className="MuiTypography-root MuiLink-root MuiLink-underlineHover MuiTypography-colorPrimary"
                                    href="/products"
                                >
                                    <div className="MuiBox-root css-axw7ok">
                                        <span className="styles_viewmore__xWSnO">
                                            Xem tất cả
                                        </span>

                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                        >
                                            <path
                                                d="M16.006 11.0818C16.5418 11.5767 16.5418 12.4233 16.006 12.9182L11.5982 16.99C10.7976 17.7295 9.5 17.1617 9.5 16.0718V7.9282C9.5 6.83829 10.7976 6.27045 11.5982 7.01001L16.006 11.0818Z"
                                                fill="#005C29"
                                            />
                                            <rect
                                                x="0.5"
                                                y="0.5"
                                                width="23"
                                                height="23"
                                                rx="11.5"
                                                stroke="#005C29"
                                            />
                                        </svg>
                                    </div>
                                </a>
                            </div>
                            <ListProductByCate
                                categoryId={product.product_category?.id || product.product_category_id}
                                currentProductId={product.id}
                            />
                        </section>
                    </div>
                </div>
            </div>
        </Layout>
    );
}